/*
  localSoloGame.ts
  Copyright (c) 2024 Human Cube Inc.

  Support for locally calculated solo games.
*/

import cloneDeep from 'clone-deep';

import { store } from '../app/store';
import { aiTurn } from '../bfai/ai_turn';
import { executeTurn, startGame } from '../bfcore/bf_turn';
import { decodeGameString, encodeGameString } from '../bfcore/gameStringDecodeEncode';
import { establishRootState } from '../bfworking/workingUtil';
import { generateSoloGameRandom, SoloGameCreateParams } from '../map/mapGenerate';
import actions from '../reducers/actions';
import mainModes from '../reducers/mainModes';
import { soloGameSave } from './localSoloGameDB';
import { Game, PlayerNormalTurn, PlayerPickTurn } from '../bfcore/gameTypes';
import BF from '../bfcore/bfconst1';
import { generatePickCapital, generatePickZone } from '../bfcore/bf_defaultpick';


export function localSoloGameCreateRandom (
  localPlayerIndex: number, params: SoloGameCreateParams,
) {
  // Create a new random map and solo game. Register local player and start it.
  const { uid } = params;
  const game = generateSoloGameRandom(params);
  const gameID = game._id;
  startGame(game);
  soloGameSave(uid, game);  // Save to browser.
  store.dispatch({type: actions.SET_GAME, game, localPlayerIndex});
  const gameStatuses = [decodeGameString(encodeGameString(game))];
  store.dispatch({type: actions.SET_GAME_STATUS, data: gameStatuses});
  store.dispatch({type: actions.GO_HOME});
  return gameID;
}


export const localSoloGameCalculate = (
  game: Game,
  uid: number,
  localPlayerIndex: number,
  playersTurn: PlayerPickTurn | PlayerNormalTurn,
) => {
  // For client side only SOLO games, progress the game with local calculations only.
  const turns = [];
  for(let p = 1; p <= game.players; p++) {
    if(game.required & (1 << p)) {
      if(p === localPlayerIndex) {
        turns[p] = playersTurn;
      }
      else {
        const thisPlayerWorking = establishRootState(game, p);
        turns[p] = aiTurn(thisPlayerWorking, game, true);
      }
    }
  }
  const nextGame = cloneDeep(game);
  executeTurn(nextGame, turns);

  // For picking turns, continue to execute turns until it is the local player's turn:
  while(nextGame.turn >= BF.TURN_PICK_ZONE && nextGame.turn <= BF.TURN_PICK_CAPITAL) {
    if(nextGame.required & (1 << localPlayerIndex)) {
      break;  // It is local player's turn, so stop advancing turns and let them play.
    }
    const turns = [];
    for(let p = 1; p <= nextGame.players; p++) {
      if(nextGame.required & (1 << p)) {
        if(nextGame.turn === BF.TURN_PICK_CAPITAL) {
          turns[p] = generatePickCapital(nextGame, p);
        }
        else {
          turns[p] = generatePickZone(nextGame, p);
        }
      }
    }
    executeTurn(nextGame, turns);
  }

  soloGameSave(uid, nextGame);  // Save to browser.
  store.dispatch({type: actions.SET_GAME, game: nextGame, localPlayerIndex});
  const gameStatuses = [decodeGameString(encodeGameString(nextGame))];
  store.dispatch({type: actions.SET_GAME_STATUS, data: gameStatuses});
  store.dispatch({type: actions.SET_MAIN_MODE, mode: mainModes.GAME_REPLAY});
};
